import React, { useCallback, useEffect, useRef, useState } from "react";
import { SIP_WS_URL } from "../../config/sip";

// npm i sip.js
// Serve your app over HTTPS. Your PBX must expose wss://<pbx>:8089/ws with a valid TLS cert.

export default function Softphone() {
  // Load extension data from localStorage (set during login)
  const loadExtensionData = () => {
    try {
      const stored = localStorage.getItem('extension');
      if (stored) {
        const ext = JSON.parse(stored);
        return {
          extensionNumber: ext.extensionNumber || '101',
          sipUsername: ext.sipUsername || ext.extensionNumber || '101',
          sipPassword: ext.sipPassword || 'VeryStrongSecret!',
          displayName: ext.displayName || 'User',
          isEnabled: ext.isEnabled !== false
        };
      }
    } catch (e) {
      console.error('Failed to load extension data:', e);
    }
    return null;
  };

  const extensionData = loadExtensionData();
  const isExtensionEnabled = extensionData?.isEnabled !== false;

  // Default to env-backed WS/WSS URL, but allow user to change it
  const [server, setServer] = useState(SIP_WS_URL);
  const [user, setUser] = useState(extensionData?.sipUsername || "1001");
  const [pass, setPass] = useState(extensionData?.sipPassword || "1001");
  const [dest, setDest] = useState("600");

  const [status, setStatus] = useState(isExtensionEnabled ? "Ready to connect" : "Extension not enabled");
  const [logs, setLogs] = useState([]);
  const [connected, setConnected] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [muted, setMuted] = useState(false);

  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const logRef = useRef(() => {});
  const connectRef = useRef(() => {});

  // Lazy-load SIP.js only in the browser
  const SIPRef = useRef(null);
  const autoConnectAttempted = useRef(false);
  
  useEffect(() => {
    (async () => {
      if (!SIPRef.current) {
        const mod = await import("sip.js");
        SIPRef.current = mod;
      }
    })();
  }, []);

  // Auto-connect if extension is enabled
  useEffect(() => {
    if (isExtensionEnabled && extensionData && !autoConnectAttempted.current && SIPRef.current && !registered) {
      autoConnectAttempted.current = true;
      logRef.current(`📞 Auto-connecting extension ${extensionData.extensionNumber} (${extensionData.displayName})`);
      // Small delay to ensure SIP.js is fully loaded
      setTimeout(() => {
        connectRef.current();
      }, 1000);
    }
  }, [isExtensionEnabled, extensionData, registered]);

  const log = useCallback((msg) => {
    setLogs((prev) => [...prev, `${new Date().toLocaleTimeString()}  ${msg}`]);
  }, []);

  logRef.current = log;

  const attachRemote = useCallback((e) => {
    const stream = e.streams && e.streams[0];
    if (audioRef.current && stream) {
      audioRef.current.srcObject = stream;
    }
  }, []);

  const connect = useCallback(async () => {
    try {
      const SIP = SIPRef.current;
      if (!SIP) return log("SIP.js not loaded yet");

      const host = new URL(server).hostname;
      const uri = SIP.UserAgent.makeURI(`sip:${user}@${host}`);

      const ua = new SIP.UserAgent({
        uri,
        authorizationUsername: user,
        authorizationPassword: pass,
        transportOptions: { 
          server,
          connectionTimeout: 15,
          keepAliveInterval: 30
        },
        sessionDescriptionHandlerFactoryOptions: {
          peerConnectionConfiguration: {
            iceServers: [
              { urls: ["stun:stun.l.google.com:19302"] }
            ]
          },
          constraints: { audio: true, video: false },
        },
        contactName: user,
        displayName: extensionData?.displayName || user,
        hackIpInContact: true,
        hackWssInTransport: true,
        userAgentString: 'WebRTC-SIP-Client/1.0',
        logLevel: 'debug'
      });

      // Transport events
      ua.transport.onConnect = () => {
        log("WSS connected");
        setConnected(true);
      };
      ua.transport.onDisconnect = () => {
        log("WSS disconnected");
        setConnected(false);
        setRegistered(false);
      };

      ua.delegate = {
        onInvite: (invitation) => {
          log("Incoming call...");
          sessionRef.current = invitation;
          invitation.delegate = { onTrack: (ev) => attachRemote(ev) };
          invitation.stateChange.addListener((s) => log("Call state: " + s));
          invitation.accept({
            sessionDescriptionHandlerOptions: {
              constraints: { audio: true, video: false },
            },
          });
        },
        onRegistering: () => setStatus("Registering..."),
        onRegistered: () => {
          setStatus("Registered");
          setRegistered(true);
          log("Registered");
        },
        onRegistrationFailed: (e) => {
          setStatus("Registration failed");
          log("Registration failed: " + (e ? JSON.stringify(e) : "unknown"));
        },
        onUnregistered: () => {
          setStatus("Unregistered");
          setRegistered(false);
        },
      };

      await ua.start();
      await ua.register();
      uaRef.current = ua;
    } catch (err) {
      log("Connect error: " + (err && err.message ? err.message : String(err)));
    }
  }, [attachRemote, extensionData?.displayName, log, pass, server, user]);

  connectRef.current = connect;

  const makeCall = useCallback(async () => {
    try {
      const SIP = SIPRef.current;
      if (!uaRef.current || !SIP) return log("Not connected");
      const target = SIP.UserAgent.makeURI(
          `sip:${dest}@${uaRef.current.configuration.uri.host}`
      );
      const inviter = new SIP.Inviter(uaRef.current, target, {
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });
      inviter.delegate = { onTrack: (e) => attachRemote(e) };
      inviter.stateChange.addListener((s) => log("Call state: " + s));
      await inviter.invite();
      sessionRef.current = inviter;
      setStatus("Calling " + dest);
    } catch (err) {
      log("Call error: " + (err && err.message ? err.message : String(err)));
    }
  }, [attachRemote, dest, log]);

  const hangup = useCallback(async () => {
    const s = sessionRef.current;
    if (!s) return;
    try {
      if (s.bye) await s.bye();
      if (s.dispose) await s.dispose();
      sessionRef.current = null;
      setStatus("Hung up");
      log("Hung up");
    } catch (e) {
      log("Hangup error: " + (e && e.message ? e.message : String(e)));
    }
  }, [log]);

  const toggleMute = useCallback(() => {
    const s = sessionRef.current;
    if (!s) return;
    const pc = s.sessionDescriptionHandler && s.sessionDescriptionHandler.peerConnection;
    if (!pc) return;
    pc.getSenders().forEach((sender) => {
      const t = sender.track;
      if (t && t.kind === "audio") t.enabled = muted; // flip below
    });
    setMuted((m) => !m);
    log(!muted ? "Muted" : "Unmuted");
  }, [log, muted]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      (async () => {
        try {
          if (sessionRef.current) {
            if (sessionRef.current.bye) await sessionRef.current.bye();
            if (sessionRef.current.dispose) await sessionRef.current.dispose();
            sessionRef.current = null;
          }
          if (uaRef.current) {
            if (uaRef.current.unregister) await uaRef.current.unregister();
            if (uaRef.current.stop) await uaRef.current.stop();
            uaRef.current = null;
          }
        } catch {}
      })();
    };
  }, []);

  return (
      <div className="mx-auto max-w-3xl p-6">
        <h2 className="text-2xl font-semibold mb-4">Issabel Web SIP Phone (chan_sip)</h2>

        {/* Extension Info */}
        {extensionData && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">Extension: {extensionData.extensionNumber}</h3>
                <p className="text-sm text-gray-600">{extensionData.displayName}</p>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                isExtensionEnabled 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {isExtensionEnabled ? '✓ Enabled' : '✗ Disabled'}
              </div>
            </div>
          </div>
        )}

        {!isExtensionEnabled && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
            <p className="text-yellow-800">⚠️ Your extension is not enabled for call center operations. Please contact your administrator.</p>
          </div>
        )}

        <div className="space-y-3">
          <input
              className="w-full rounded-xl border p-3"
              placeholder="WSS (wss://pbx.example.com:8089/ws)"
              value={server}
              onChange={(e) => setServer(e.target.value)}
              disabled={!isExtensionEnabled}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
                className="rounded-xl border p-3"
                placeholder="SIP User (e.g., 1001)"
                value={user}
                onChange={(e) => setUser(e.target.value)}
                disabled={!isExtensionEnabled}
            />
            <input
                className="rounded-xl border p-3"
                type="password"
                placeholder="Password"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                disabled={!isExtensionEnabled}
            />
          </div>
          <button
              className="w-full rounded-2xl border bg-black text-white p-3 disabled:opacity-60"
              onClick={connect}
              disabled={!isExtensionEnabled || registered}
          >
            {registered ? "Registered" : "Connect / Register"}
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
                className="rounded-xl border p-3"
                placeholder="Dial number (e.g., 600 echo or 1002)"
                value={dest}
                onChange={(e) => setDest(e.target.value)}
                disabled={!isExtensionEnabled || !registered}
            />
            <button
                className="rounded-2xl border bg-black text-white p-3 disabled:opacity-60"
                onClick={makeCall}
                disabled={!isExtensionEnabled || !registered}
            >
              Call
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
                className="rounded-2xl border p-3"
                onClick={hangup}
                disabled={!isExtensionEnabled || !sessionRef.current}
            >
              Hang Up
            </button>
            <button
                className="rounded-2xl border p-3"
                onClick={toggleMute}
                disabled={!isExtensionEnabled || !sessionRef.current}
            >
              {muted ? "Unmute" : "Mute"}
            </button>
          </div>
        </div>

        <div className="mt-6">
          <h3 className="text-lg font-medium">Status</h3>
          <div className="rounded-xl border p-3 bg-gray-50">
            <div className="text-sm mb-2">
              {status}
              {connected ? " • WSS" : ""}
            </div>
            <div className="whitespace-pre-wrap text-sm max-h-52 overflow-auto">
              {logs.map((l, i) => (
                  <div key={i}>{l}</div>
              ))}
            </div>
          </div>
        </div>

        <audio ref={audioRef} autoPlay />
        <div className="mt-6 text-sm text-gray-500">
          <p>
            Note: Use <strong>HTTPS</strong> for your React app. Your PBX must expose
            <code> wss://&lt;pbx&gt;:8089/ws</code> with a valid TLS certificate. In <strong>chan_sip</strong>, enable
            <code> transport=ws,wss</code>, SRTP (SDES or DTLS), <code>avpf</code>, <code>force_avp</code>,{" "}
            <code>icesupport</code>, <code>rtcp_mux</code>, and open RTP <code>10000–20000/UDP</code>.
          </p>
        </div>
      </div>
  );
}
